import React from 'react';
import fetch from 'utilities/fetch';
import { paths } from 'config/path_helpers';
import { TopicOverviewContextInterface } from './types';

const TopicOverviewContext = React.createContext<TopicOverviewContextInterface | null>(null);

export const useTopicOverviewContext = () => {
  const context = React.useContext(TopicOverviewContext);
  if (context === undefined) {
    throw new Error(
      'You must wrap your page with <TopicOverviewProvider /> in order to useTopicOverviewContext().'
    );
  }
  return context;
};

export const TopicOverviewProvider: React.FC = ({ children }) => {
  const cache = React.useRef({});
  const [data, setData] = React.useState({ math: [], verbal: [], writing: [] });
  const [addingTopic, setAddingTopic] = React.useState(false);
  const [openPerformanceExplanationModal, setOpenPerformanceExplanationModal] = React.useState(false);

  const fetchData = React.useCallback(
    (subsection) => {
      if (cache.current[subsection]) {
        const cacheData = cache.current[subsection];
        setData({ ...data, [subsection]: cacheData });
      } else {
        fetch(paths.api.subsections(subsection)).then((response) => {
          setData({ ...data, [subsection]: response.subsections });
          cache.current[subsection] = response.subsections;
          return response.subsections;
        });
      }
    },
    [data]
  );

  const processMathData = React.useCallback(() => {
    const reducedData = data['math'].reduce(
      (reducedTopics, topic) => {
        reducedTopics[topic.isStrategyFocused ? 0 : 1].push(topic);
        return reducedTopics;
      },
      [[], []]
    );
    return reducedData;
  }, [data]);

  const addATopic = React.useCallback((segmentId: number, subsection: string, callBack: () => void) => {
    setAddingTopic(true);
    fetch(paths.api.studyPlanItems(), {
      method: 'POST',
      body: JSON.stringify({
        studyPlanItem: {
          contentBlockId: segmentId
        }
      })
    }).then(() => {
      setAddingTopic(false);
      callBack();
      cache.current[subsection] = null;
      fetchData(subsection);
    });
  }, []);

  const togglePerformanceExplanationModal = React.useCallback(() => {
    setOpenPerformanceExplanationModal(!openPerformanceExplanationModal);
  }, [openPerformanceExplanationModal]);

  const providerValue = React.useMemo(
    () => ({
      data,
      addingTopic,
      openPerformanceExplanationModal,
      togglePerformanceExplanationModal,
      fetchData,
      processMathData,
      addATopic
    }),
    [
      data,
      addingTopic,
      openPerformanceExplanationModal,
      togglePerformanceExplanationModal,
      fetchData,
      processMathData,
      addATopic
    ]
  );

  return <TopicOverviewContext.Provider value={providerValue}>{children}</TopicOverviewContext.Provider>;
};
